"use client";

import { Suspense, useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { toast } from "sonner";
import { LoginForm } from "@/modules/auth/components/LoginForm";
import { CompanySelector } from "@/modules/auth/components/CompanySelector";
import { login, type CompanyOption } from "@/modules/auth/api";

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="space-y-6"><div className="h-24 animate-pulse rounded bg-muted" /></div>}>
      <LoginPageInner />
    </Suspense>
  );
}

function LoginPageInner() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchParams = useSearchParams();

  // Mostrar mensaje si la sesión expiró
  useEffect(() => {
    if (searchParams.get('expired') === '1') {
      setError('Tu sesión ha expirado. Por favor inicia sesión nuevamente.');
    }
  }, [searchParams]);

  // Estado para selector multi-empresa
  const [isSelectingCompany, setIsSelectingCompany] = useState(false);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [tempToken, setTempToken] = useState("");
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");

  const handleLogin = async (values: { email: string; password: string; remember?: boolean }) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await login({
        email: values.email,
        password: values.password,
        remember: values.remember,
      });

      // Multi-empresa: mostrar selector
      if (result.require_company_selection && result.companies && result.temp_token) {
        setCompanies(result.companies);
        setTempToken(result.temp_token);
        setFirstName(result.first_name || "");
        setEmail(result.email);
        setIsSelectingCompany(true);
        setIsLoading(false);
        return;
      }

      // Login directo (1 empresa) → redirect
      setIsLoading(false);
      if (typeof window !== "undefined") {
        const currentPath = window.location.pathname;
        if (currentPath.startsWith("/app/")) {
          window.location.href = "/app/dashboard";
        } else {
          window.location.href = "/dashboard";
        }
      }
    } catch {
      toast.error("No pudimos iniciar sesión. Verifica tus credenciales o consulta al administrador.");
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setIsSelectingCompany(false);
    setCompanies([]);
    setTempToken("");
    setFirstName("");
    setEmail("");
    setError(null);
  };

  // Selector de empresa (full-screen dentro del layout)
  if (isSelectingCompany) {
    return (
      <CompanySelector
        companies={companies}
        tempToken={tempToken}
        firstName={firstName}
        email={email}
        onBack={handleBackToLogin}
      />
    );
  }

  // Login normal
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground md:text-2xl">Inicia sesión en tu cuenta</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Ingresa con tus credenciales corporativas. Si no reconoces estas pantallas, contacta a tu administrador.
        </p>
      </div>
      <LoginForm
        onSubmit={handleLogin}
        isLoading={isLoading}
        error={error}
      />
    </div>
  );
}
